import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import APIManager from "../../../managers/APIManager";
import Loading from "../../common/Loading";
import Button from "../../common/Button";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import LanguageSelector from "../../common/LanguageSelector";
import TemplateItem from "../templates/TemplateItem";
import Collapse from "../../common/Collapse";
import Form from "../../common/Form";
import Page from "../../common/Page";
import { useStyleSettings } from "../../../hooks";
import { toast } from "react-toastify";
import {
  renderTemplateStylingSettings,
  templateDefaultStyleSettings,
} from "../templates";
import Switch from "react-switch";

const AIVideoSettings = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [
    saveVideoCreationSettingsLoading,
    setSaveVideoCreationSettingsLoading,
  ] = useState(false);

  const siteVirgulId = useSelector(
    (state) => state.site?.selectedSite?.virgulId
  );
  const [selectedVideoTemplate, setSelectedVideoTemplate] = useState(null);
  const [videoTemplates, setVideoTemplates] = useState([]);
  const [autoGenerateVideo, setAutoGenerateVideo] = useState(false);

  // Use the reducer-based hook for style settings
  const { styleSettings, mergeStyleSettings, handleStyleChange } =
    useStyleSettings();

  const [contentSelection, setContentSelection] = useState("last4");
  const [regenerationFrequency, setRegenerationFrequency] = useState("daily");
  const [regenerationHour, setRegenerationHour] = useState("0");
  const [regenerationDay, setRegenerationDay] = useState("monday");

  const saveVideoCreationSettings = useCallback(async ({ isActive, silent = false }) => {
    if (!silent) {
      setSaveVideoCreationSettingsLoading(true);
    }
    try {
      await APIManager.saveAIVideoCreationSettings({
        siteVirgulId,
        templateName: selectedVideoTemplate.name,
        templateSettings: styleSettings,
        contentSettings: {
          contentSelection,
        },
        regenerationSettings: {
          regenerationFrequency,
          regenerationHour:
            regenerationFrequency === "daily" ? regenerationHour : undefined,
          regenerationDay:
            regenerationFrequency === "weekly" ? regenerationDay : undefined,
        },
        isActive: isActive ?? autoGenerateVideo,
      });
      if (!silent) {
        toast.success(t("video creation settings saved successfully"));
      }
    } catch (error) {
      console.error("Error saving video creation settings:", error);
      toast.error(t("error saving video creation settings"));
    } finally {
      setSaveVideoCreationSettingsLoading(false);
    }
  }, [
    siteVirgulId,
    selectedVideoTemplate,
    styleSettings,
    contentSelection,
    regenerationFrequency,
    regenerationHour,
    regenerationDay,
    autoGenerateVideo,
  ]);

  const fetchVideoTemplates = useCallback(async () => {
    try {
      const fetchedVideoTemplatesRes = await APIManager.getAIVideoTemplates({
        siteVirgulId,
      });
      setVideoTemplates(fetchedVideoTemplatesRes?.data || []);
      return fetchedVideoTemplatesRes?.data || [];
    } catch (error) {
      console.error("Error fetching video templates:", error);
      setVideoTemplates([]);
    }
  }, [siteVirgulId]);

  const fetchVideoCreationSettings = useCallback(
    async (videoTemplates) => {
      try {
        const fetchedVideoCreationSettingsRes =
          await APIManager.getAIVideoCreationSettings({
            siteVirgulId,
          });
        setSelectedVideoTemplate(
          videoTemplates.find(
            (template) =>
              template.name ===
              fetchedVideoCreationSettingsRes?.data?.templateName
          )
        );
        setContentSelection(
          fetchedVideoCreationSettingsRes?.data?.contentSettings
            ?.contentSelection
        );
        const regenerationSettings =
          fetchedVideoCreationSettingsRes?.data?.regenerationSettings || {};
        setRegenerationFrequency(
          regenerationSettings.regenerationFrequency ||
          fetchedVideoCreationSettingsRes?.data?.regenerationFrequency
        );
        setRegenerationHour(regenerationSettings.regenerationHour || "0");
        setRegenerationDay(regenerationSettings.regenerationDay || "monday");
        setAutoGenerateVideo(
          fetchedVideoCreationSettingsRes?.data?.isActive || false
        );
        mergeStyleSettings({
          ...templateDefaultStyleSettings(
            fetchedVideoCreationSettingsRes?.data?.templateName
          ),
          ...fetchedVideoCreationSettingsRes?.data?.templateSettings,
        });
      } catch (error) {
        console.error("Error fetching video creation settings:", error);
      }
    },
    [siteVirgulId]
  );

  useEffect(() => {
    fetchVideoTemplates().then((videoTemplates) => {
      fetchVideoCreationSettings(videoTemplates).then(() => {
        setLoading(false);
      });
    });
  }, [fetchVideoTemplates, fetchVideoCreationSettings]);

  if (loading) {
    return <Loading />;
  }

  return (
    <Page className="pb-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <Button
            className="mr-4"
            variant="secondary"
            icon={faArrowLeft}
            onClick={() => navigate(-1)}
          >
            {t("back")}
          </Button>

          <h1 className="text-3xl font-bold text-gray-900">
            {t("video creation settings")}
          </h1>
        </div>
        <div className="flex items-center">
          <label className="text-sm font-semibold text-gray-600 flex items-center">
            <span className="mr-2">
              {t("is auto generate video active")}
            </span>
            <Switch
              name="autoGenerateVideo"
              checked={autoGenerateVideo}
              onChange={(checked) => {
                setAutoGenerateVideo(checked);
                saveVideoCreationSettings({ isActive: checked, silent: true });
              }}
            />
          </label>
          <LanguageSelector />
        </div>
      </div>
      <Collapse
        title={
          <div className="font-semibold text-gray-800 text-2xl">
            {t("video style & branding")}
          </div>
        }
        defaultOpen
      >
        <Collapse
          title={
            <div className="font-semibold text-gray-800 text-xl">
              {t("video templates")}
            </div>
          }
          className="mb-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {videoTemplates.map((template) => (
              <TemplateItem
                key={template.name}
                name={template.name}
                videoUrl={template.videoUrl}
                selected={selectedVideoTemplate?.name === template.name}
                onClick={() => {
                  setSelectedVideoTemplate(template);
                  // Merge template default settings if available
                  if (template.defaultStyleSettings) {
                    mergeStyleSettings(template.defaultStyleSettings);
                  }
                }}
              />
            ))}
          </div>
        </Collapse>

        {selectedVideoTemplate &&
          renderTemplateStylingSettings(
            selectedVideoTemplate.name,
            styleSettings,
            handleStyleChange
          )}
      </Collapse>
      <Collapse
        title={
          <div className="font-semibold text-gray-800 text-2xl">
            {t("content & frequency")}
          </div>
        }
        className="mt-6"
        defaultOpen={!!selectedVideoTemplate}
        disabled={!selectedVideoTemplate}
      >
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Form.Select
            name="contentSelection"
            label={t("content selection")}
            className="w-full"
            value={contentSelection}
            onChange={(e) => setContentSelection(e.target.value)}
          >
            <option value="last4">
              {t("last {{count}} news", { count: 4 })}
            </option>
            <option value="last8">
              {t("last {{count}} news", { count: 8 })}
            </option>
          </Form.Select>

          <Form.Select
            name="regenerationFrequency"
            label={t("video regeneration frequency")}
            className="w-full"
            value={regenerationFrequency}
            onChange={(e) => setRegenerationFrequency(e.target.value)}
          >
            <option value="daily">{t("daily")}</option>
            <option value="weekly">{t("weekly")}</option>
          </Form.Select>

          {regenerationFrequency === "daily" && (
            <Form.Select
              name="regenerationHour"
              label={t("regeneration hour")}
              className="w-full"
              value={regenerationHour}
              onChange={(e) => setRegenerationHour(e.target.value)}
            >
              {Array.from({ length: 24 }, (_, i) => (
                <option key={i} value={i}>
                  {i.toString().padStart(2, "0")}:00
                </option>
              ))}
            </Form.Select>
          )}

          {regenerationFrequency === "weekly" && (
            <Form.Select
              name="regenerationDay"
              label={t("regeneration day")}
              className="w-full"
              value={regenerationDay}
              onChange={(e) => setRegenerationDay(e.target.value)}
            >
              <option value="monday">{t("monday")}</option>
              <option value="tuesday">{t("tuesday")}</option>
              <option value="wednesday">{t("wednesday")}</option>
              <option value="thursday">{t("thursday")}</option>
              <option value="friday">{t("friday")}</option>
              <option value="saturday">{t("saturday")}</option>
              <option value="sunday">{t("sunday")}</option>
            </Form.Select>
          )}
        </div>
      </Collapse>
      <Button
        className="mt-6 ml-auto"
        variant="inverse_primary"
        onClick={saveVideoCreationSettings}
        disabled={!selectedVideoTemplate}
        loading={saveVideoCreationSettingsLoading}
      >
        {t("save settings")}
      </Button>
    </Page>
  );
};

export default AIVideoSettings;
